import { computed } from 'vue'
import { useRestaurantStore } from '@/stores/restaurant'
import { formatPrice, getCurrencySymbol } from '@/utils/formatters'

/** Default currency code used when restaurant has none configured */
const DEFAULT_CURRENCY_CODE = 'LKR'

/**
 * Centralized currency composable.
 *
 * Provides helpers for:
 * - Formatting prices with the correct currency symbol
 * - Converting item prices to the restaurant's default currency
 * - Checking if conversion is available for a given currency
 *
 * All currency display and conversion logic should go through this composable
 * so future changes (e.g. locale-aware formatting, new currencies) only need
 * one update.
 */
export function useCurrency() {
  const restaurantStore = useRestaurantStore()

  /** Restaurant's base currency code (e.g. 'LKR') */
  const defaultCurrencyCode = computed(
    () => restaurantStore.defaultCurrencyCode || DEFAULT_CURRENCY_CODE
  )

  /** Today's exchange rates from the restaurant store: { USD: 320, EUR: 355, … } */
  const currencyRates = computed(() => restaurantStore.currencyRates)

  /** Display symbol for the restaurant's default currency */
  const defaultCurrencySymbol = computed(() => getCurrencySymbol(defaultCurrencyCode.value))

  // ── Formatting ──────────────────────────────────────────────────────────

  /**
   * Format a price in its **native** currency (the currency the item is priced in).
   * Use this on menu cards and the customize drawer.
   *
   * @param price   - The numeric price value
   * @param code    - ISO 4217 currency code of the item (defaults to restaurant's default)
   */
  function formatItemPrice(price: number | null | undefined, code?: string): string {
    return formatPrice(price, code || defaultCurrencyCode.value)
  }

  /**
   * Format a price in the restaurant's **default** currency.
   * Use this in the cart, order summary, and confirmation screens
   * where all amounts are already converted.
   */
  function formatDefaultPrice(price: number | null | undefined): string {
    return formatPrice(price, defaultCurrencyCode.value)
  }

  // ── Conversion ──────────────────────────────────────────────────────────

  /**
   * Check whether a given currency can be converted to the restaurant default.
   * Returns true if:
   * - The currency IS the default (no conversion needed), or
   * - A rate exists in `currencyRates`
   */
  function canConvert(fromCurrencyCode: string): boolean {
    if (!fromCurrencyCode) return true
    const normalised = fromCurrencyCode.toUpperCase()
    if (normalised === defaultCurrencyCode.value.toUpperCase()) return true
    return normalised in (currencyRates.value ?? {})
  }

  /**
   * Convert a price from a foreign currency to the restaurant's default currency.
   *
   * If the item is already in the default currency, returns as-is.
   * If no rate is found, returns `null` (caller should block add-to-cart).
   *
   * @param price             - Amount in the source currency
   * @param fromCurrencyCode  - ISO 4217 code of the source currency
   * @returns The converted price, or `null` if the rate is missing
   */
  function convertToDefaultCurrency(
    price: number,
    fromCurrencyCode: string | undefined
  ): number | null {
    if (!fromCurrencyCode) return price
    const normalised = fromCurrencyCode.toUpperCase()
    const defaultCode = defaultCurrencyCode.value.toUpperCase()

    // Already in default currency — no conversion needed
    if (normalised === defaultCode) return price

    const rate = currencyRates.value?.[normalised]
    if (rate === undefined || rate === null || rate <= 0) return null

    // rate means: 1 unit of `fromCurrencyCode` = `rate` units of default currency
    return Math.round(price * rate * 100) / 100
  }

  return {
    // State
    defaultCurrencyCode,
    defaultCurrencySymbol,
    currencyRates,

    // Formatting
    formatItemPrice,
    formatDefaultPrice,
    getCurrencySymbol,

    // Conversion
    canConvert,
    convertToDefaultCurrency,
  }
}
